import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationRulesTab = () => {
  const [rules, setRules] = useState([
    {
      id: 1,
      name: "Warranty Expiration Alert",
      description: "Alert when asset warranty is about to expire",
      trigger: "warranty_expiring",
      threshold: "30",
      thresholdUnit: "days",
      channels: ["email", "in_app"],
      recipients: ["it_managers", "asset_owners"],
      isActive: true,
      priority: "medium",
      createdDate: "2024-01-15"
    },
    {
      id: 2,
      name: "Audit Deadline Warning",
      description: "Notify when quarterly audit deadline approaches",
      trigger: "audit_deadline",
      threshold: "7",
      thresholdUnit: "days",
      channels: ["email", "sms", "in_app"],
      recipients: ["audit_team", "it_managers"],
      isActive: true,
      priority: "high",
      createdDate: "2024-01-15"
    },
    {
      id: 3,
      name: "Asset Status Change",
      description: "Notify when asset status changes to repair or retired",
      trigger: "status_change",
      threshold: "0",
      thresholdUnit: "immediate",
      channels: ["email", "in_app"],
      recipients: ["asset_owners", "it_support"],
      isActive: true,
      priority: "medium",
      createdDate: "2024-02-01"
    },
    {
      id: 4,
      name: "Location Mismatch Alert",
      description: "Alert when physical asset location doesn't match records",
      trigger: "location_mismatch",
      threshold: "0",
      thresholdUnit: "immediate",
      channels: ["email", "in_app"],
      recipients: ["security_team", "facilities"],
      isActive: true,
      priority: "high",
      createdDate: "2024-02-10"
    },
    {
      id: 5,
      name: "Bulk Asset Changes",
      description: "Notify administrators of bulk asset modifications",
      trigger: "bulk_changes",
      threshold: "10",
      thresholdUnit: "assets",
      channels: ["email"],
      recipients: ["system_admins"],
      isActive: false,
      priority: "low",
      createdDate: "2024-03-01"
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: "",
    description: "",
    trigger: "",
    threshold: "",
    thresholdUnit: "days",
    channels: [],
    recipients: [],
    isActive: true,
    priority: "medium"
  });

  const triggerOptions = [
    { value: "warranty_expiring", label: "Warranty Expiring" },
    { value: "audit_deadline", label: "Audit Deadline" },
    { value: "status_change", label: "Asset Status Change" },
    { value: "location_mismatch", label: "Location Mismatch" },
    { value: "bulk_changes", label: "Bulk Asset Changes" },
    { value: "maintenance_due", label: "Maintenance Due" },
    { value: "license_expiring", label: "License Expiring" },
    { value: "asset_overdue", label: "Asset Check-in Overdue" }
  ];

  const thresholdUnitOptions = [
    { value: "immediate", label: "Immediate" },
    { value: "minutes", label: "Minutes" },
    { value: "hours", label: "Hours" },
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "assets", label: "Number of Assets" }
  ];

  const channelOptions = [
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    { value: "in_app", label: "In-App Notification" },
    { value: "webhook", label: "Webhook" },
    { value: "slack", label: "Slack" }
  ];

  const recipientOptions = [
    { value: "system_admins", label: "System Administrators" },
    { value: "it_managers", label: "IT Managers" },
    { value: "asset_owners", label: "Asset Owners" },
    { value: "audit_team", label: "Audit Team" },
    { value: "it_support", label: "IT Support" },
    { value: "security_team", label: "Security Team" },
    { value: "facilities", label: "Facilities Team" },
    { value: "procurement", label: "Procurement Team" }
  ];

  const priorityOptions = [
    { value: "low", label: "Low Priority" },
    { value: "medium", label: "Medium Priority" },
    { value: "high", label: "High Priority" },
    { value: "critical", label: "Critical Priority" }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-error bg-error/10';
      case 'high':
        return 'text-warning bg-warning/10';
      case 'medium':
        return 'text-accent bg-accent/10';
      case 'low':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const handleAddRule = () => {
    if (newRule?.name && newRule?.trigger) {
      const rule = {
        id: Date.now(),
        ...newRule,
        createdDate: new Date()?.toISOString()?.split('T')?.[0]
      };
      setRules(prev => [...prev, rule]);
      setNewRule({
        name: "",
        description: "",
        trigger: "",
        threshold: "",
        thresholdUnit: "days",
        channels: [],
        recipients: [],
        isActive: true,
        priority: "medium"
      });
      setShowAddModal(false);
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setNewRule({ ...rule });
    setShowAddModal(true);
  };

  const handleUpdateRule = () => {
    setRules(prev =>
      prev?.map(rule =>
        rule?.id === editingRule?.id ? { ...newRule, id: editingRule?.id } : rule
      )
    );
    setEditingRule(null);
    setNewRule({
      name: "",
      description: "",
      trigger: "",
      threshold: "",
      thresholdUnit: "days",
      channels: [],
      recipients: [],
      isActive: true,
      priority: "medium"
    });
    setShowAddModal(false);
  };

  const handleDeleteRule = (id) => {
    if (window.confirm('Are you sure you want to delete this notification rule?')) {
      setRules(prev => prev?.filter(rule => rule?.id !== id));
    }
  };

  const handleToggleRule = (id) => {
    setRules(prev =>
      prev?.map(rule =>
        rule?.id === id ? { ...rule, isActive: !rule?.isActive } : rule
      )
    );
  };

  const handleTestRule = (rule) => {
    console.log('Testing notification rule:', rule?.name);
    // Simulate sending test notification
    alert(`Test notification sent for rule: ${rule?.name}`);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingRule(null);
    setNewRule({
      name: "",
      description: "",
      trigger: "",
      threshold: "",
      thresholdUnit: "days",
      channels: [],
      recipients: [],
      isActive: true,
      priority: "medium"
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Notification Rules</h3>
          <p className="text-sm text-muted-foreground">Configure automated alerts and notification triggers</p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowAddModal(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add Rule
        </Button>
      </div>
      {/* Rules List */}
      <div className="space-y-4">
        {rules?.map((rule) => (
          <div key={rule?.id} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  rule?.isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  <Icon name="Bell" size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-card-foreground">{rule?.name}</h4>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(rule?.priority)}`}>
                      {rule?.priority}
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      rule?.isActive 
                        ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                    }`}>
                      {rule?.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{rule?.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Trigger:</span>
                      <p className="font-medium text-card-foreground">
                        {triggerOptions?.find(t => t?.value === rule?.trigger)?.label}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Threshold:</span>
                      <p className="font-medium text-card-foreground">
                        {rule?.threshold} {rule?.thresholdUnit}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Channels:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rule?.channels?.map((channel) => (
                          <span
                            key={channel}
                            className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded"
                          >
                            {channelOptions?.find(c => c?.value === channel)?.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4">
                <Checkbox
                  checked={rule?.isActive}
                  onChange={() => handleToggleRule(rule?.id)}
                  label="Enabled"
                />
                <span className="text-sm text-muted-foreground">
                  Created: {rule?.createdDate}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestRule(rule)}
                  iconName="Send"
                  iconPosition="left"
                >
                  Test
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditRule(rule)}
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteRule(rule?.id)}
                  iconName="Trash2"
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">
                {editingRule ? 'Edit Notification Rule' : 'Add New Notification Rule'}
              </h3>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Rule Name"
                  type="text"
                  value={newRule?.name}
                  onChange={(e) => setNewRule(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="e.g., Warranty Expiration Alert"
                  required
                />
                
                <Select
                  label="Priority Level"
                  options={priorityOptions}
                  value={newRule?.priority}
                  onChange={(value) => setNewRule(prev => ({ ...prev, priority: value }))}
                />
              </div>

              <Input
                label="Description"
                type="text"
                value={newRule?.description}
                onChange={(e) => setNewRule(prev => ({ ...prev, description: e?.target?.value }))}
                placeholder="Brief description of when this rule triggers"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  label="Trigger Event"
                  options={triggerOptions}
                  value={newRule?.trigger}
                  onChange={(value) => setNewRule(prev => ({ ...prev, trigger: value }))}
                  required
                />
                
                <Input
                  label="Threshold Value"
                  type="number"
                  value={newRule?.threshold}
                  onChange={(e) => setNewRule(prev => ({ ...prev, threshold: e?.target?.value }))}
                  placeholder="0"
                />
                
                <Select
                  label="Threshold Unit"
                  options={thresholdUnitOptions}
                  value={newRule?.thresholdUnit}
                  onChange={(value) => setNewRule(prev => ({ ...prev, thresholdUnit: value }))}
                />
              </div>

              <Select
                label="Notification Channels"
                description="Select how notifications should be delivered"
                options={channelOptions}
                value={newRule?.channels}
                onChange={(value) => setNewRule(prev => ({ ...prev, channels: value }))}
                multiple
                required
              />

              <Select
                label="Recipients"
                description="Select who should receive these notifications"
                options={recipientOptions}
                value={newRule?.recipients}
                onChange={(value) => setNewRule(prev => ({ ...prev, recipients: value }))}
                multiple
                required
              />

              <Checkbox
                label="Enable Rule"
                description="Start monitoring and sending notifications immediately"
                checked={newRule?.isActive}
                onChange={(e) => setNewRule(prev => ({ ...prev, isActive: e?.target?.checked }))}
              />
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={editingRule ? handleUpdateRule : handleAddRule}
                disabled={!newRule?.name || !newRule?.trigger || newRule?.channels?.length === 0}
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationRulesTab;